import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { ActivationModal } from "@/components/substitution/activation-modal";
import { useAuthData } from "@/context/auth-data-provider";
import { FALLBACK_REGIONAL_LABEL } from "@/context/utils/substitution-format";
import { formatDate } from "@/utils/format-date";
import { hasSubstitutionClaim } from "@/context/utils/token-substitution";
import { GroupsEnum } from "@/enum/groups";
import { useReconcile } from "@/hooks/use-reconcile";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import type { SubstitutionInfo } from "@/@types/user";

const HERDED_GROUP: string = GroupsEnum.RETAGUARDA_DE_LOJA;
const ACTIVATION_TITLE = "Ativar substituição";

export function useSubstitutionGate() {
  const navigate = useNavigate();
  const authData = useAuthData();
  const reconcile = useReconcile();
  const { handleModal } = useDialogModal();

  const substitution: SubstitutionInfo | null = authData?.substitution ?? null;
  const isPendingRelogin: boolean =
    Boolean(substitution?.active) && !hasSubstitutionClaim();

  const guardedNavigate = useCallback(
    (path: string, groupRequired: string[]): boolean => {
      const isHerded: boolean = groupRequired.includes(HERDED_GROUP);

      if (!isPendingRelogin || !isHerded) {
        navigate(path);
        return true;
      }

      const activateAndNavigate = async (): Promise<void> => {
        handleModal({ isOpen: false });
        const reconciled: boolean = await reconcile();
        if (reconciled) {
          navigate(path);
        }
      };

      handleModal({
        isOpen: true,
        title: ACTIVATION_TITLE,
        onClose: () => {
          void reconcile();
        },
        element: (
          <ActivationModal
            regionalName={substitution?.entity?.name ?? FALLBACK_REGIONAL_LABEL}
            dataFim={formatDate(substitution?.period?.end, { fallback: "" })}
            onConfirm={() => void activateAndNavigate()}
          />
        ),
      });

      return false;
    },
    [isPendingRelogin, navigate, handleModal, reconcile, substitution],
  );

  return { guardedNavigate, isPendingRelogin };
}
